import { useExternalAccessRights, useVenuePosIntegrationStatus } from 'common/hooks';
import { isRetailVenue } from 'common/utils';
import { usePermissions } from 'common/wolt-permissions';
import {
  hasCampaignViewPermission,
  hasMenuContentEditPermission,
  hasMenuEditorPermission,
  hasPurchasesPermission,
  hasSettlementsPermission,
  hasTranslationPermission,
  hasVenueDetailsViewPermission,
  hasVenueInsightsPermission,
  hasVenueUserManagementPermission,
} from 'common/wolt-permissions/permissionUtils';
import useBestOfWoltFeatureFlag from 'modules/best-of-wolt/hooks/useBestOfWoltFeatureFlag';

import { useExperienceModuleContext } from '../context';
import { ExperienceEntity, ExperienceEntityType, ExperienceVenueEntity } from '../types';

export const useCanViewBestOfWolt = (
  experienceVenueEntities: ExperienceVenueEntity[],
): { hasPermissionsForVenues: boolean; isBestOfWoltEnabled: boolean } => {
  const {
    experienceEntity: { experienceEntityId },
    country,
    city,
  } = useExperienceModuleContext();

  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();

  const { isBestOfWoltEnabled } = useBestOfWoltFeatureFlag({
    venueId: experienceEntityId,
    venueCountry: country,
    venueCity: city ?? '',
    enabled: !!city,
  });

  const hasPermissionsForVenues = experienceVenueEntities.some(experienceVenueEntity => {
    const venue = experienceVenueEntity.experienceEntityObject;
    return hasVenueInsightsPermission(permissions, externalAccessRights, venue) && venue.type !== 'daas';
  });

  return { hasPermissionsForVenues, isBestOfWoltEnabled };
};

export const useCanViewBusinessDetails = (experienceVenueEntities: ExperienceVenueEntity[]): boolean => {
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();

  const hasPermissionsForVenues = experienceVenueEntities.some(experienceVenueEntity => {
    return hasVenueDetailsViewPermission(
      permissions,
      externalAccessRights,
      experienceVenueEntity.experienceEntityObject,
    );
  });

  return hasPermissionsForVenues;
};

export const useCanViewPurchase = (experienceVenueEntities: ExperienceVenueEntity[]): boolean => {
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();

  const hasPermissionsForVenues = experienceVenueEntities.some(experienceVenueEntity => {
    return hasPurchasesPermission(permissions, externalAccessRights, experienceVenueEntity.experienceEntityObject);
  });

  return hasPermissionsForVenues;
};

export const useCanViewTranslation = (
  experienceEntity: ExperienceEntity | null,
): { isLoading: boolean; hasPermissions: boolean } => {
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();

  const { data: venuePosIntegrationStatus, isLoading } = useVenuePosIntegrationStatus(
    experienceEntity && experienceEntity.experienceEntityType === ExperienceEntityType.VENUE
      ? experienceEntity.experienceEntityObject.id
      : null,
  );

  if (!experienceEntity) {
    return { isLoading, hasPermissions: false };
  }

  if (experienceEntity.experienceEntityType !== ExperienceEntityType.VENUE) {
    return { isLoading, hasPermissions: true };
  }

  const venue = experienceEntity.experienceEntityObject;

  return {
    isLoading,
    hasPermissions:
      hasTranslationPermission(permissions, externalAccessRights, venue) &&
      hasMenuContentEditPermission(permissions, externalAccessRights, venue, venuePosIntegrationStatus) &&
      hasMenuEditorPermission(permissions, externalAccessRights, venue) &&
      venue.type !== 'daas',
  };
};

export const useCanViewPickingRoute = (experienceVenueEntities: ExperienceVenueEntity[]): boolean => {
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();

  const hasPermissionsForVenues = experienceVenueEntities.some(experienceVenueEntity => {
    const venue = experienceVenueEntity.experienceEntityObject;
    return (
      hasPurchasesPermission(permissions, externalAccessRights, venue) && isRetailVenue(venue) && venue.type !== 'daas'
    );
  });

  return hasPermissionsForVenues;
};

export const useCanViewSettlements = (experienceVenueEntities: ExperienceVenueEntity[]): boolean => {
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();

  const hasPermissionsForVenues = experienceVenueEntities.some(experienceVenueEntity => {
    return hasSettlementsPermission(permissions, externalAccessRights, experienceVenueEntity.experienceEntityObject);
  });

  return hasPermissionsForVenues;
};

export const useCanViewInsights = (experienceEntity: ExperienceEntity) => {
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();

  if (experienceEntity.experienceEntityType === ExperienceEntityType.VENUE) {
    return (
      hasVenueInsightsPermission(permissions, externalAccessRights, experienceEntity.experienceEntityObject) &&
      experienceEntity.experienceEntityObject.type !== 'daas'
    );
  }

  return true;
};

export const useCanViewUserManagement = (experienceEntity: ExperienceEntity) => {
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();

  if (experienceEntity.experienceEntityType === ExperienceEntityType.VENUE) {
    return hasVenueUserManagementPermission(permissions, externalAccessRights, experienceEntity.experienceEntityObject);
  }

  return true;
};

export const useCanViewCampaigns = (
  experienceEntity: ExperienceEntity,
  experienceVenueEntities: ExperienceVenueEntity[],
) => {
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();

  if (experienceEntity.experienceEntityType !== ExperienceEntityType.MERCHANT) {
    return experienceVenueEntities.some(experienceVenueEntity => {
      return hasCampaignViewPermission(permissions, externalAccessRights, experienceVenueEntity.experienceEntityObject);
    });
  }

  return true;
};
